import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { linkify } from '@/lib/linkify';
import { usePresentation } from '@/context/PresentationContext';
import { FreeText, MemberName, HouseholdName } from '@/components/Present';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { DASHBOARD } from '@/constants/testIds';
import { WarningOctagon, HourglassMedium, Checks, CalendarBlank, ArrowRight, HouseLine, Sparkle } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

function greeting() {
    const h = new Date().getHours();
    if (h < 5) return 'Hello';
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
}

function StatCard({ label, value, icon: Icon, tid, accent, to }) {
    const inner = (
        <>
            <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
                <Icon size={18} className={accent ? 'text-[#F59E0B]' : 'text-primary'} />
            </div>
            <div className="mt-3 font-heading text-5xl text-slate-900 leading-none">{value}</div>
        </>
    );
    const cls = "rounded-xl border border-slate-200 bg-card p-4 sm:p-5 shadow-sm block hover:shadow-md hover:border-primary/50 transition-shadow duration-150 min-h-[100px]";
    if (to) {
        return <Link to={to} data-testid={tid} className={cls}>{inner}</Link>;
    }
    return <div data-testid={tid} className={cls}>{inner}</div>;
}

function ReviewDialog({ log, onDone }) {
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState('');
    const submit = async () => {
        try {
            await api.post(`/patient-logs/${log.log_id}/review`, { note });
            toast.success('Marked as reviewed');
            setOpen(false); setNote('');
            onDone?.();
        } catch { toast.error('Failed'); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid={DASHBOARD.reviewButton} size="sm" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shrink-0 min-h-[44px] sm:min-h-0">
                    Mark as reviewed
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Mark this as reviewed</DialogTitle>
                </DialogHeader>
                <div className="text-sm text-slate-600 whitespace-pre-wrap max-h-48 overflow-auto border border-slate-200 rounded-lg p-3 bg-slate-50">
                    <FreeText>{linkify(log.note)}</FreeText>
                </div>
                <Textarea
                    data-testid={DASHBOARD.reviewNote}
                    placeholder="Optional note (what you did, follow-up)…"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                />
                <DialogFooter>
                    <Button data-testid={DASHBOARD.reviewSubmit} className="rounded-full bg-primary text-primary-foreground" onClick={submit}>
                        Mark as reviewed
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function Dashboard() {
    const qc = useQueryClient();
    const { user } = useAuth();
    const { data, isLoading } = useQuery({
        queryKey: ['dashboard'],
        queryFn: async () => (await api.get('/dashboard/summary')).data,
    });
    const { data: households = [] } = useQuery({
        queryKey: ['facilities'],
        queryFn: async () => (await api.get('/facilities')).data,
    });

    if (isLoading || !data) {
        return <div className="p-10 text-slate-500 text-sm">Loading…</div>;
    }

    const { stats, flagged_logs, stale_logs, overdue_tasks, upcoming_appointments } = data;
    const refresh = () => {
        qc.invalidateQueries({ queryKey: ['dashboard'] });
        qc.invalidateQueries({ queryKey: ['facilities'] });
    };
    const firstName = (user?.name || '').split(' ')[0] || '';
    const isEmpty = households.length === 0 && stats.open_loops === 0 && stats.upcoming_appointments === 0;

    const seed = async () => {
        try {
            const { data } = await api.post('/onboarding/sample-household');
            if (data.already) {
                toast.info('You already have a sample household — check your households list.');
            } else {
                toast.success('Sample household added — meet the Riveras!');
            }
            refresh();
        } catch { toast.error('Could not load sample'); }
    };

    return (
        <div data-testid={DASHBOARD.root} className="px-4 sm:px-6 md:px-10 py-6 md:py-8 max-w-7xl">
            <header className="mb-6 md:mb-8">
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Home</div>
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-slate-900 mt-1">
                    {greeting()}{firstName ? `, ${firstName}` : ''}.
                </h1>
                <p className="text-slate-600 mt-2 max-w-2xl text-sm md:text-base">
                    Here&apos;s what needs a gentle nudge today. Everything with a flag waits for you to mark it as reviewed.
                </p>
            </header>

            <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <StatCard label="Waiting to hear back" value={stats.open_loops} icon={HourglassMedium} tid={DASHBOARD.statOpenLoops} to="/dashboard/waiting" />
                <StatCard label="To-dos" value={stats.open_tasks} icon={Checks} tid={DASHBOARD.statOpenTasks} to="/dashboard/todos" />
                <StatCard label="Needs your attention" value={stats.flagged_pending} icon={WarningOctagon} tid={DASHBOARD.statFlagged} accent to="/dashboard/attention" />
                <StatCard label="Coming up this week" value={stats.upcoming_appointments} icon={CalendarBlank} tid={DASHBOARD.statAppointments} to="/dashboard/upcoming" />
            </section>

            <section className="grid lg:grid-cols-3 gap-6 mt-8">
                <div data-testid={DASHBOARD.triageList} className="lg:col-span-2 rounded-xl border border-slate-200 bg-card shadow-lg">
                    <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.2em] text-[#F59E0B] font-semibold">Needs your attention</div>
                            <div className="font-heading text-2xl text-slate-900 mt-0.5">The short list</div>
                        </div>
                        <WarningOctagon size={22} className="text-[#F59E0B]" />
                    </div>
                    <div className="divide-y divide-slate-200">
                        {flagged_logs.length === 0 && stale_logs.length === 0 && overdue_tasks.length === 0 && (
                            <div className="px-5 py-14 text-center text-slate-500">
                                <div className="font-heading text-xl text-slate-800">Nothing waiting on you right now.</div>
                                <div className="text-sm mt-1">Take a breath — you&apos;re caught up.</div>
                            </div>
                        )}

                        {flagged_logs.map(l => (
                            <div key={l.log_id} className="px-4 sm:px-5 py-4 flex flex-col sm:flex-row sm:items-start gap-3">
                                <div className="flex items-start gap-3 min-w-0 flex-1">
                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-[#F59E0B] text-black mt-1 shrink-0">! Attention</span>
                                    <div className="min-w-0 flex-1">
                                        <Link to={`/members/${l.patient_id}`} className="text-sm font-medium text-slate-900 hover:underline">
                                            <MemberName name={l.patient_name} />
                                        </Link>
                                        <div className="text-xs text-slate-500">{l.entry_type} · {new Date(l.entry_at).toLocaleString()}</div>
                                        <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap line-clamp-3"><FreeText>{l.note}</FreeText></div>
                                    </div>
                                </div>
                                <ReviewDialog log={l} onDone={refresh} />
                            </div>
                        ))}

                        {stale_logs.map(l => (
                            <div key={l.log_id} className="px-5 py-4 flex items-start gap-3">
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-slate-200 text-slate-700 mt-1 shrink-0">Waiting</span>
                                <div className="min-w-0 flex-1">
                                    <Link to={`/members/${l.patient_id}`} className="text-sm font-medium text-slate-900 hover:underline">
                                        <MemberName name={l.patient_name} />
                                    </Link>
                                    <div className="text-xs text-slate-500">Waiting to hear back since {new Date(l.entry_at).toLocaleDateString()}</div>
                                    <div className="text-sm text-slate-700 mt-1 line-clamp-2"><FreeText>{l.note}</FreeText></div>
                                </div>
                            </div>
                        ))}

                        {overdue_tasks.map(t => (
                            <div key={t.task_id} className="px-5 py-4 flex items-start gap-3">
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-red-100 text-red-700 mt-1 shrink-0">Past due</span>
                                <div className="min-w-0 flex-1">
                                    <Link to={`/members/${t.patient_id}`} className="text-sm font-medium text-slate-900 hover:underline">
                                        <MemberName name={t.patient_name} />
                                    </Link>
                                    <div className="text-xs text-slate-500">Due {t.due_date}</div>
                                    <div className="text-sm text-slate-700 mt-1">{t.title}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div data-testid={DASHBOARD.upcomingList} className="rounded-xl border border-slate-200 bg-card shadow-sm">
                    <div className="px-5 py-4 border-b border-slate-200">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Coming up this week</div>
                        <div className="font-heading text-2xl text-slate-900 mt-0.5">Upcoming visits</div>
                    </div>
                    <div className="divide-y divide-slate-200">
                        {upcoming_appointments.length === 0 && (
                            <div className="px-5 py-10 text-center text-sm text-slate-500">Nothing scheduled — quiet week ahead.</div>
                        )}
                        {upcoming_appointments.map(a => (
                            <div key={a.appointment_id} className="px-5 py-4">
                                <div className="text-sm font-medium text-slate-900">{a.title}</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    <MemberName name={a.patient_name} /> · {a.scheduled_at ? new Date(a.scheduled_at).toLocaleString() : 'TBD'}
                                </div>
                                {a.provider_name && <div className="text-xs text-slate-500">{a.provider_name}</div>}
                                <Link
                                    to={`/prep/${a.appointment_id}`}
                                    className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                >
                                    Prep for this visit <ArrowRight size={12} />
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section data-testid={DASHBOARD.householdsGrid} className="mt-10">
                <div className="flex items-end justify-between mb-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Your people</div>
                        <h2 className="font-heading text-3xl text-slate-900 mt-1">Households</h2>
                    </div>
                    <Link to="/households" className="text-sm text-primary hover:underline">See all →</Link>
                </div>

                {households.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 py-12 px-6 text-center">
                        <HouseLine size={40} className="mx-auto text-slate-400" />
                        <div className="font-heading text-2xl mt-3 text-slate-800">Add your first household to get started</div>
                        <p className="text-slate-500 text-sm mt-1 max-w-md mx-auto">
                            A household is your grandmother, your dad&apos;s house, an aunt&apos;s assisted-living community — a small group of people you look after together.
                        </p>
                        <div className="mt-5 flex items-center justify-center gap-2">
                            <Link to="/households"><Button data-testid="onboarding-add-first-household" className="rounded-full bg-primary text-primary-foreground">Add your first household</Button></Link>
                            <Button
                                data-testid="onboarding-sample-button"
                                variant="outline" className="rounded-full"
                                onClick={seed}
                            >
                                <Sparkle size={14} className="mr-1" /> Explore with sample data
                            </Button>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-4">The sample household is clearly labeled and deletable in one tap.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {households.map(f => (
                            <Link key={f.facility_id} to={`/households/${f.facility_id}`}
                                className="group rounded-xl border border-slate-200 bg-card p-5 shadow-sm hover:border-primary transition-colors duration-150">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{f.kind}</div>
                                        <div className="font-heading text-2xl mt-1 text-slate-900"><HouseholdName name={f.name} /></div>
                                    </div>
                                    <HouseLine size={22} className="text-primary" />
                                </div>
                                <div className="mt-6 flex items-center justify-between text-sm">
                                    <span className="text-slate-600">{f.patient_count} member{f.patient_count === 1 ? '' : 's'}</span>
                                    <span className="inline-flex items-center gap-1 text-primary group-hover:translate-x-0.5 transition-transform duration-150">
                                        Open <ArrowRight size={14} />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
